export const STORAGE_KEY = 'perfect-slice.records.v1';
export const SCORE_VERSION = 2;
export const EMPTY_RECORDS = { bestScore: 0, longestStreak: 0, shapesPlayed: 0, perfectSlices: 0, legendarySlices: 0, bestDifference: null, scoreVersion: SCORE_VERSION };
export function migrateRecords(saved) {
  const result = { ...EMPTY_RECORDS };
  for (const key of Object.keys(result)) {
    if (key !== 'scoreVersion' && Number.isFinite(saved?.[key]) && saved[key] >= 0) result[key] = saved[key];
  }
  if (saved?.scoreVersion !== SCORE_VERSION) result.bestScore = 0;
  return result;
}
export function readRecords() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const result = migrateRecords(saved);
    if (saved?.scoreVersion !== SCORE_VERSION) saveRecords(result);
    return result;
  } catch { return { ...EMPTY_RECORDS }; }
}
export function saveRecords(records) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...records, scoreVersion: SCORE_VERSION })); return true; } catch { return false; }
}
// Monotone cubic Hermite interpolation: exact approved anchors, smooth slope,
// no overshoot. The tail approaches zero without rewarding large misses.
export const SCORE_ANCHORS = [[0,1000],[.1,990],[.25,970],[.5,940],[1,870],[2,700],[3,550],[5,350],[10,100],[20,8],[40,0],[100,0]];
const intervals = SCORE_ANCHORS.slice(1).map((p,i)=>p[0]-SCORE_ANCHORS[i][0]);
const secants = SCORE_ANCHORS.slice(1).map((p,i)=>(p[1]-SCORE_ANCHORS[i][1])/intervals[i]);
const slopes = SCORE_ANCHORS.map((_,i)=> {
  if(i===0) return secants[0];
  if(i===SCORE_ANCHORS.length-1) return secants.at(-1);
  if(secants[i-1]*secants[i]<=0) return 0;
  const w1=2*intervals[i]+intervals[i-1],w2=intervals[i]+2*intervals[i-1];
  return (w1+w2)/(w1/secants[i-1]+w2/secants[i]);
});
export function pointsForDifference(difference) {
  if(!Number.isFinite(difference)) return 0;
  const d=Math.max(0,difference);
  if(d>=40) return 0;
  const i=SCORE_ANCHORS.findIndex((p,j)=>j<SCORE_ANCHORS.length-1 && d>=p[0] && d<=SCORE_ANCHORS[j+1][0]);
  const h=intervals[i],t=(d-SCORE_ANCHORS[i][0])/h,t2=t*t,t3=t2*t;
  const value=(2*t3-3*t2+1)*SCORE_ANCHORS[i][1]+(t3-2*t2+t)*h*slopes[i]+(-2*t3+3*t2)*SCORE_ANCHORS[i+1][1]+(t3-t2)*h*slopes[i+1];
  return Math.min(1000,Math.max(0,Math.round(value)));
}
export function scoreCut(target, percent) {
  const difference = Math.abs(target - percent);
  return { percent, difference, points: pointsForDifference(difference), ...reactionFor(difference) };
}
export function reactionFor(difference) {
  difference = Math.max(0, difference - 1e-9);
  if (difference <= 0.1) return { tier: 'legendary', label: 'LEGENDARY SLICE', note: 'That is ridiculously good.', stars: 0 };
  if (difference <= 0.5) return { tier: 'perfect', label: 'PERFECT SLICE', note: 'Your eyes have a built-in ruler.', stars: 0 };
  if (difference <= 1) return { tier: 'incredible', label: 'INCREDIBLE', note: 'You make this look easy.', stars: 3 };
  if (difference <= 2) return { tier: 'great', label: 'GREAT CUT', note: 'A little instinct goes a long way.', stars: 2 };
  if (difference <= 3) return { tier: 'nice', label: 'NICE SLICE', note: 'You have an eye for this.', stars: 1 };
  if (difference <= 5) return { tier: 'close', label: 'SO CLOSE!', note: 'Just a tiny adjustment…', stars: 0 };
  return { tier: 'almost', label: 'ONE MORE TRY?', note: difference > 20 ? 'That looked better in your head.' : 'Your next cut could be the one.', stars: 0 };
}
export function finalizeRecords(records, best, streak) {
  return { ...records, shapesPlayed: records.shapesPlayed + 1, longestStreak: Math.max(records.longestStreak, streak) };
}
export function formatPercent(value) {
  if (value > 0 && value < 0.01) return '<0.01';
  if (value < 100 && value > 99.99) return '>99.99';
  return value.toFixed(2).replace(/0$/, '');
}
